import { Component, OnInit, inject, output } from '@angular/core';
import { loginActions, roles } from '@shared/constants';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { AuthorizeService } from '@shared/services';
import { ThemeService } from '@shared/services/theme.service';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';

interface AccountMenuItem {
  label: string;
  routerLink: string;
  state?: { local?: boolean };
}

@Component({
  selector: 'lib-login-menu',
  styleUrls: ['login-menu.component.scss'],
  templateUrl: 'login-menu.component.html',
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    RouterLink,
    AsyncPipe,
    SvgIconComponent,
    ThemeToggleComponent,
  ],
})
export class LoginMenuComponent implements OnInit {
  private readonly authorizeService = inject(AuthorizeService);

  /** The theme menu only shows when allowDarkMode is on in assets/config.json */
  public readonly isDarkModeAllowed = inject(ThemeService).isDarkModeAllowed;

  public readonly showFeedback = output();
  public roles = roles;
  public isAuthenticated$: Observable<boolean> | undefined;
  public accountMenu$: Observable<AccountMenuItem[]> | undefined;

  public ngOnInit() {
    this.isAuthenticated$ = this.authorizeService.isAuthenticated();

    this.accountMenu$ = this.authorizeService.isSsoUser$.pipe(
      map(isSsoUser =>
        [
          {
            label: 'Change password',
            routerLink: '/authentication/profile',
            when: !isSsoUser,
          },
          {
            label: 'Change areas of interest',
            routerLink: `/authentication/${loginActions.marketingPreferences}`,
            when: this.isClubHub && !isSsoUser,
          },
          {
            label: 'Change CRM system information',
            routerLink: `/authentication/${loginActions.crmSystemInformation}`,
            when: this.isClubHub && !isSsoUser,
          },
          {
            label: 'Firm details',
            routerLink: '/firmdetails',
            when: this.isClubHub || this.isIgnite,
          },
          {
            label: 'Document preferences',
            routerLink: '/documentpreferences',
            when: this.isClubHub || this.isIgnite,
          },
          {
            label: 'Fees / True cost',
            routerLink: '/productpreferences',
            when: this.isClubHub || this.isIgnite,
          },
          {
            label: 'Filter preferences',
            routerLink: '/filterpreferences',
            when: this.isClubHub || this.isIgnite,
          },
          {
            label: 'Manage two factor authentication',
            routerLink: '/managetwofactorauthentication',
            when: (this.isClubHub || this.isIgnite) && isSsoUser,
          },
          {
            label: 'Log out',
            routerLink: '/authentication/logout',
            state: { local: true },
            when: true,
          },
        ].filter(x => x.when),
      ),
    );
  }

  public hasRole(role: string) {
    return this.authorizeService.hasRole(role);
  }

  public showFeedbackModal() {
    this.showFeedback.emit(undefined);
  }

  private get isClubHub(): boolean {
    return this.authorizeService.isClubHubApp;
  }

  private get isIgnite() {
    return this.authorizeService.isIgniteApp;
  }
}
